import { _isFeDev, _log, _warn } from '@shared/aux_helper_environment';
import { _orderBy, _cloneDeep, _timeout, _toNumber } from '@shared/aux_helper_functions';
import { AddProdToShelf } from 'app/spaces/planograms-editor/store/planogram.store.state';
import {
  _getTmpProdId,
  _round,
  DEFAULT_NORMAL_SHELF_GUIDE_WIDTH,
  DEFAULT_OFFSET_MIN_PRD_SEP_X,
  DEFAULT_FINGER_SPACE,
  DEFAULT_AUTOSPACE,
  DEFAULT_MIN_RATIO_FOR_AUTOSPACE,
  _calcRotateTransforms,
  DEFAULT_PALLET_HEIGHT,
  GLOBAL_JUSTIFY_FACINGS,
  DEFAULT_MIN_RATIO_FOR_PRODUCT_INTERNAL_AUTOSPACE,
  DEFAULT_PRODUCT_INTERNAL_AUTOSPACE,
  _calcSizeProd,
} from 'app/spaces/static_common/spaces_aux_helpers';
import { _auxGetMaxRemainingWidthSpace } from './svgBaseShelf.component';
import { _resolveActiveExhibitionTypeId } from 'app/spaces/static_common/spaces_aux_exhibition_format';
import { resolveShelfCompressionForLayout } from 'app/spaces/static_common/spaces_product_compression';
import { FixedSpaceRange, resolveXAfterFixedSpaces } from 'app/spaces/static_common/spaces_fixed_space_layout';
import {
  applyCompressionToFacingWidth,
  dispatchShelfCompressionResult,
  hasShelfActiveCompression,
  isProductCompressionFeatureEnabledFromInjector,
  notifyCompressionApplied,
  notifyCompressionRejected,
  runShelfCompressionAttempt,
} from 'app/spaces/static_common/spaces_product_compression.integration';

const EXHIBITION_TYPE_SRP = 2;

const _getExhibitionFormatByTypeId = (prod: any, exhibitionTypeId: number) => {
  const uoe = prod?.unitOfExhibition;
  if (uoe?.exhibitionTypeId === exhibitionTypeId) return uoe;

  const formats = prod?.itemData?.exhibitionFormats;
  if (!Array.isArray(formats)) return null;
  return formats.find(f => f?.exhibitionTypeId === exhibitionTypeId) || null;
};

let _stacksLyingDownFirst = true; // Variable global para prender la feature que al tumbar productos el de debajo de todo quede parado (ej: Pañales)
export const setStacksLyingDownFirst = val => (_stacksLyingDownFirst = val);

/**
 * Simula el posicionamiento secuencial de productos normales considerando espacios fijos.
 * Retorna true si todos los productos (incluyendo newProdWidth) caben sin superponerse.
 */
const _simulateProductsFitOnShelf = (prods, shelfWidth, marginLeft, marginRight, newProdWidth, existingProdId = null) => {
  const sepX = DEFAULT_OFFSET_MIN_PRD_SEP_X;
  const marginLeftOffset = marginLeft || 0;
  const usableWidth = shelfWidth - (marginRight || 0);

  const fixedSpaces: FixedSpaceRange[] = [];
  const prodWidths = [];

  (prods || []).forEach(prd => {
    if (!prd || !prd.itemData) return;
    const isFixed = _isRealFixedEmptySpace(prd);
    if (isFixed) {
      const start = prd.fixedX || 0;
      fixedSpaces.push({ start, end: start + (prd.fixedWidth || 0) });
    } else if (prd.id !== existingProdId) {
      const w = _calcSizeProd(prd).w;
      if (w > 0) prodWidths.push(w);
    }
  });

  if (newProdWidth > 0) prodWidths.push(newProdWidth);
  fixedSpaces.sort((a, b) => a.start - b.start);

  let lastEndX = marginLeftOffset;

  for (const pw of prodWidths) {
    let x = _round(lastEndX + sepX);
    if (lastEndX === marginLeftOffset) x = marginLeftOffset + sepX;

    x = resolveXAfterFixedSpaces(x, pw, fixedSpaces, sepX, DEFAULT_OFFSET_MIN_PRD_SEP_X);

    if (x + pw > usableWidth) return false;
    lastEndX = x + pw;
  }

  return true;
};

export { _simulateProductsFitOnShelf };

export const _canFitProductOnNormalShelfSequential = (shelf: any, prodsOnShelf: any[], prod: any): boolean => {
  if (!shelf || !prod) return false;
  if (shelf.type && shelf.type !== 'normalShelf-level') return false;

  if (shelf.__shelfAvoidCheckSpace === true) return true;

  const size = prod.size || _calcSizeProd(prod);
  const shelfHeight = Number(shelf.height || shelf.size?.h || 0);

  if (size.h + DEFAULT_FINGER_SPACE > shelfHeight) return false;
  if (prod.isPallet === true && size.h + DEFAULT_PALLET_HEIGHT > shelfHeight) return false;

  const shelfWidth = Number(shelf.width || shelf.size?.w || 0);
  return _simulateProductsFitOnShelf(prodsOnShelf, shelfWidth, shelf.marginLeft || 0, shelf.marginRight || 0, size.w, null);
};

export const _fitProductsSequentiallyOnNormalShelf = (
  shelf: any,
  existingProds: any[],
  productsToPlace: any[],
  excludeProdIds: Set<string | number> = new Set()
): { fitting: any[]; failed: any[] } => {
  const fitting: any[] = [];
  const failed: any[] = [];
  const _isExcluded = (id: any): boolean => excludeProdIds.has(id) || excludeProdIds.has(String(id));
  let virtualProds = (existingProds || []).filter(p => p && !_isExcluded(p.id));

  (productsToPlace || []).forEach(prod => {
    if (!prod) return;
    if (_canFitProductOnNormalShelfSequential(shelf, virtualProds, prod)) {
      fitting.push(prod);
      virtualProds = [...virtualProds, { ...prod, size: prod.size || _calcSizeProd(prod) }];
    } else {
      failed.push(prod);
    }
  });

  return { fitting, failed };
};

/** Posición X secuencial más cercana al punto de soltado (equivalente standalone a `_aux_getNearNextPosition_x`). */
export const _getNearNextPositionX = (
  prods: any[],
  marginLeft: number,
  position: { x: number },
  width: number,
  excludeProdIds: Set<string | number> = new Set()
): { x: number; obj: any | null } => {
  const sepX = DEFAULT_OFFSET_MIN_PRD_SEP_X;
  if (!prods?.length) return { x: (marginLeft || 0) + sepX, obj: null };
  const _isExcluded = (id: any): boolean => excludeProdIds.has(id) || excludeProdIds.has(String(id));

  let tmpNearObj: any = null;
  let tmpNearlastDiff = 0;
  prods.forEach(obj => {
    if (!obj?.position || _isExcluded(obj.id)) return;
    const offsetX = obj.position.x + obj.size.w * 0.5 + sepX;
    const diff = position.x + width * 0.5 - offsetX;
    if (diff > 0 && (diff < tmpNearlastDiff || tmpNearlastDiff === 0)) {
      tmpNearlastDiff = diff;
      tmpNearObj = obj;
    }
  });

  const leftMargin = marginLeft || 0;
  return {
    x: tmpNearObj ? tmpNearObj.position.x + tmpNearObj.size.w + sepX : leftMargin + sepX,
    obj: tmpNearObj,
  };
};

/** CATMAN-B018: valida altura de un producto en estante normal. */
const _canFitProductHeightOnNormalShelf = (shelf: any, prod: any): boolean => {
  if (!shelf || !prod) return false;
  const size = prod.size || _calcSizeProd(prod);
  const shelfHeight = Number(shelf.height || shelf.size?.h || 0);
  if (size.h + DEFAULT_FINGER_SPACE > shelfHeight) return false;
  if (prod.isPallet === true && size.h + DEFAULT_PALLET_HEIGHT > shelfHeight) return false;
  return true;
};

/**
 * CATMAN-B018: coloca productos en la zona del drop (cursor), no al final del estante.
 * Respeta orden de selección y admite fit parcial.
 */
export const _fitProductsAtDropOnNormalShelf = (
  shelf: any,
  existingProds: any[],
  productsToPlace: any[],
  dropCenterX: number,
  excludeProdIds: Set<string | number> = new Set()
): { fitting: any[]; failed: any[] } => {
  const fitting: any[] = [];
  const failed: any[] = [];

  if (!shelf || shelf.type !== 'normalShelf-level') {
    return { fitting: [], failed: productsToPlace || [] };
  }

  if (shelf.__shelfAvoidCheckSpace === true) {
    return { fitting: [...(productsToPlace || [])], failed: [] };
  }

  const shelfWidth = Number(shelf.width || shelf.size?.w || 0);
  const marginLeft = shelf.marginLeft || 0;
  const marginRight = shelf.marginRight || 0;
  const sepX = DEFAULT_OFFSET_MIN_PRD_SEP_X;
  const availableTotal = _getAvailableSpaceInZoneAtX(
    dropCenterX,
    existingProds || [],
    shelfWidth,
    marginLeft,
    marginRight,
    null,
    excludeProdIds
  );

  let usedWidth = 0;
  (productsToPlace || []).forEach(prod => {
    if (!prod) return;
    if (!_canFitProductHeightOnNormalShelf(shelf, prod)) {
      failed.push(prod);
      return;
    }
    const w = Number(_calcSizeProd(prod).w ?? 0);
    const needed = (fitting.length > 0 ? sepX : 0) + w;
    if (usedWidth + needed <= availableTotal) {
      fitting.push(prod);
      usedWidth += needed;
    } else {
      failed.push(prod);
    }
  });

  return { fitting, failed };
};

/** Productos normales en la zona del drop, ordenados por X. */
const _getNormalProdsInDropZone = (
  prods: any[],
  dropCenterX: number,
  shelfWidth: number,
  marginLeft: number,
  marginRight: number
): any[] => {
  const normal = (prods || [])
    .filter(p => p?.position && !_isRealFixedEmptySpace(p) && !_isEmptySpacePlaceholder(p))
    .sort((a, b) => (a.position?.x ?? 0) - (b.position?.x ?? 0));

  if (!shelfWidth) return normal;

  const zones = _buildShelfZoneRanges(prods, shelfWidth, marginLeft, marginRight);
  const zoneIdx = _zoneIndexForCenterX(dropCenterX, zones);
  const _w = (p: any) => Number(_calcSizeProd(p).w ?? 0);
  return normal.filter(p => _zoneIndexForCenterX(_compactCenterX(p, _w(p)), zones) === zoneIdx);
};

/**
 * CATMAN-B018: calcula el order del primer producto en un drop múltiple.
 * Usa el borde izquierdo del drop (no solo el centro) para detectar inserción al inicio de la zona.
 */
export const _resolveFirstOrderForDropAtX = (
  prodsForOrder: any[],
  dropCenterX: number,
  dropLeftX: number,
  firstProdWidth: number,
  marginLeft: number,
  excludeProdIds: Set<string | number> = new Set(),
  shelfWidth = 0,
  marginRight = 0
): number => {
  const sepX = DEFAULT_OFFSET_MIN_PRD_SEP_X;
  const fixedSpaceToLeft = _getFixedSpaceToLeftOfPosition(dropCenterX, prodsForOrder);
  if (fixedSpaceToLeft) {
    return _getOrderForDropAfterFixedSpace(fixedSpaceToLeft, prodsForOrder, dropCenterX);
  }

  const prodsInZone = _getNormalProdsInDropZone(prodsForOrder, dropCenterX, shelfWidth, marginLeft, marginRight);
  const leftmost = prodsInZone[0];
  if (leftmost) {
    const leftmostLeft = leftmost.position?.x ?? 0;
    const leftmostW = Number(_calcSizeProd(leftmost).w ?? leftmost?.size?.w ?? 0);
    // Umbral tolerante: si el centro del drop cae dentro del ancho del primer producto,
    // interpretamos intención de "poner al inicio" para evitar que quede en 2do lugar.
    const startThresholdX = leftmostLeft + Math.max(sepX, leftmostW);
    if (dropCenterX <= startThresholdX) {
      return Number(leftmost.order ?? 0) - 0.5;
    }
  }

  const nearNext = _getNearNextPositionX(prodsForOrder, marginLeft, { x: dropLeftX }, firstProdWidth, excludeProdIds);
  return nearNext.obj ? nearNext.obj.order + 0.5 : -0.5;
};

/**
 * CATMAN-B018: asigna order y position (solo al primero) para soltar múltiples productos en el cursor.
 */
export const _prepareMassDropProds = (
  prodsToMove: any[],
  existingProds: any[],
  dropCenterX: number,
  marginLeft = 0,
  excludeProdIds: Set<string | number> = new Set(),
  shelfWidth = 0,
  marginRight = 0
): any[] => {
  if (!prodsToMove?.length) return [];

  const _isExcluded = (id: any): boolean => excludeProdIds.has(id) || excludeProdIds.has(String(id));
  const prodsForOrder = (existingProds || []).filter(p => p && !_isExcluded(p.id));
  const first = prodsToMove[0];
  const firstSize = first.size || _calcSizeProd(first);
  const tmpPosition = { x: dropCenterX - firstSize.w / 2, y: 0 };

  const firstOrder = _resolveFirstOrderForDropAtX(
    prodsForOrder,
    dropCenterX,
    tmpPosition.x,
    firstSize.w,
    marginLeft,
    excludeProdIds,
    shelfWidth,
    marginRight
  );

  return prodsToMove.map((prod, i) => {
    const prepared = _cloneDeep(prod);
    if (i === 0) {
      prepared.order = firstOrder;
      prepared.position = { x: _round(tmpPosition.x), y: _round(tmpPosition.y) };
    } else {
      delete prepared.position;
      prepared.order = firstOrder + i * 0.001;
    }
    return prepared;
  });
};

/**
 * CATMAN-B014: Calcula el espacio LIBRE disponible en la ZONA donde está el producto.
 * Considera los espacios fijos como divisores de zonas (un producto no puede cruzarlos) y
 * calcula el espacio restante usando anchos compactos (como si dispersar estuviera apagado),
 * restando TODOS los productos de la zona (incluido el producto actual con su footprint actual).
 *
 * Semántica equivalente a `_getMaxRemainingWidthSpace` pero acotada a la zona del producto:
 * - Para un producto a la izquierda del espacio fijo: espacio libre entre margen izq. y el espacio fijo.
 * - Para un producto a la derecha del espacio fijo: espacio libre entre el espacio fijo y el final del mueble.
 *
 * @param prodId ID del producto actual
 * @param prods Array de todos los productos del estante (incluye fijos)
 * @param shelfWidth Ancho total del estante
 * @param marginLeft Margen izquierdo del estante
 * @param marginRight Margen derecho del estante
 * @returns Espacio libre en la zona del producto (calculado sin dispersión)
 */
export const _getAvailableSpaceInZoneForProduct = (
  prodId: string,
  prods: any[],
  shelfWidth: number,
  marginLeft: number,
  marginRight: number
): number => {
  const sepX = DEFAULT_OFFSET_MIN_PRD_SEP_X;
  const normalProds = (prods || []).filter(p => p && !_isRealFixedEmptySpace(p) && !_isEmptySpacePlaceholder(p));
  const zones = _buildShelfZoneRanges(prods, shelfWidth, marginLeft, marginRight);

  const _w = (p: any): number => Number(_calcSizeProd(p).w ?? 0);
  const _zoneForProd = (p: any): number => _zoneIndexForCenterX(_compactCenterX(p, _w(p)), zones);

  const currentProd = normalProds.find(p => String(p.id) === String(prodId));
  const zoneIdx = currentProd ? _zoneForProd(currentProd) : zones.length - 1;
  const zone = zones[zoneIdx];
  const zoneWidth = zone.end - zone.start;

  const prodsInZone = normalProds.filter(p => _zoneForProd(p) === zoneIdx);
  const occupied = prodsInZone.reduce((acc, p) => acc + _w(p) + sepX, 0);

  return Math.max(0, zoneWidth - occupied);
};

/**
 * CATMAN-B014: Calcula el espacio libre en la ZONA donde se suelta un producto (según el centro X
 * del drop), respetando los espacios vacíos fijos como barrera. Se usa para validar el arrastre/drop.
 *
 * @param dropCenterX Centro X donde se suelta el producto
 * @param prods Array de todos los productos del estante
 * @param shelfWidth Ancho del estante
 * @param marginLeft Margen izquierdo del estante
 * @param marginRight Margen derecho del estante
 * @param excludeId Id del producto que se está moviendo (se excluye del cálculo), si aplica
 */
export const _getAvailableSpaceInZoneAtX = (
  dropCenterX: number,
  prods: any[],
  shelfWidth: number,
  marginLeft: number,
  marginRight: number,
  excludeId: string | null = null,
  excludeIds: Set<string | number> | null = null
): number => {
  const sepX = DEFAULT_OFFSET_MIN_PRD_SEP_X;
  const excludeSet = new Set<string>(excludeIds ? Array.from(excludeIds).map(id => String(id)) : []);
  if (excludeId != null) excludeSet.add(String(excludeId));
  const normalProds = (prods || []).filter(
    p => p && !_isRealFixedEmptySpace(p) && !_isEmptySpacePlaceholder(p) && !excludeSet.has(String(p.id))
  );
  const zones = _buildShelfZoneRanges(prods, shelfWidth, marginLeft, marginRight);

  const _w = (p: any): number => Number(_calcSizeProd(p).w ?? 0);
  const _zoneForProd = (p: any): number => _zoneIndexForCenterX(_compactCenterX(p, _w(p)), zones);

  const dropZoneIdx = _zoneIndexForCenterX(dropCenterX, zones);
  const dropZone = zones[dropZoneIdx];
  const zoneWidth = dropZone.end - dropZone.start;

  const prodsInZone = normalProds.filter(p => _zoneForProd(p) === dropZoneIdx);
  const occupied = prodsInZone.reduce((acc, p) => acc + _w(p) + sepX, 0);

  return Math.max(0, zoneWidth - occupied);
};

/**
 * CATMAN-B014: Indica si la zona donde está el producto está acotada a la derecha por un
 * espacio vacío fijo (true) o por el borde del estante (false).
 *
 * Se usa para el modo "no validar tamaños": un producto en la última zona (borde del estante)
 * puede sobresalir fuera del mueble, pero un producto cuya zona termina en un espacio fijo
 * NUNCA puede crecer hacia/dentro del espacio fijo.
 */
export const _isProductZoneBoundedByFixedSpace = (
  prodId: string,
  prods: any[],
  shelfWidth: number,
  marginLeft: number,
  marginRight: number
): boolean => {
  const zones = _buildShelfZoneRanges(prods, shelfWidth, marginLeft, marginRight);

  const cur = (prods || []).find(p => String(p?.id) === String(prodId));
  if (!cur) return zones[zones.length - 1]?.boundedByFixed ?? false;

  const w = Number(_calcSizeProd(cur).w ?? 0);
  const zoneIdx = _zoneIndexForCenterX(_compactCenterX(cur, w), zones);
  return zones[zoneIdx]?.boundedByFixed ?? false;
};

/**
 * CATMAN-B014: Determina si una posición de drop está a la derecha de algún espacio fijo.
 * Se usa para respetar la intención del usuario al arrastrar productos.
 *
 * @param dropX Posición X donde se suelta el producto
 * @param prods Array de todos los productos del estante
 * @returns El espacio fijo más cercano a la izquierda del dropX, o null si no hay ninguno
 */
export const _getFixedSpaceToLeftOfPosition = (dropX: number, prods: any[]): any => {
  if (!prods || prods.length === 0) return null;

  const fixedSpaces = prods.filter(p => _isRealFixedEmptySpace(p));
  if (fixedSpaces.length === 0) return null;

  fixedSpaces.sort((a, b) => (b.fixedX || 0) - (a.fixedX || 0));

  for (const fs of fixedSpaces) {
    const fsEnd = (fs.fixedX || 0) + (fs.fixedWidth || 0);
    if (fsEnd <= dropX) {
      return fs;
    }
  }

  return null;
};

/**
 * CATMAN-B014: Calcula el order para un producto que debe colocarse a la derecha de un espacio fijo.
 * Respeta la posición X de soltado: inserta el producto en el lugar correcto de la secuencia de la
 * zona (entre los vecinos adecuados) en vez de forzarlo siempre como el primero de la zona.
 *
 * @param fixedSpace El espacio fijo a la izquierda del cual queremos ubicar el producto
 * @param prods Array de todos los productos del estante
 * @param dropCenterX Centro X donde el usuario soltó el producto
 * @returns El order calculado para el nuevo producto
 */
export const _getOrderForDropAfterFixedSpace = (fixedSpace: any, prods: any[], dropCenterX = 0): number => {
  if (!fixedSpace || !prods || prods.length === 0) return 0;

  const fsEnd = (fixedSpace.fixedX || 0) + (fixedSpace.fixedWidth || 0);

  const prodsToRightOfFixedSpace = prods
    .filter(p => {
      if (_isRealFixedEmptySpace(p)) return false;
      const prodX = p.position?.x || 0;
      return prodX >= fsEnd - 1;
    })
    .sort((a, b) => (a.position?.x || 0) - (b.position?.x || 0));

  if (prodsToRightOfFixedSpace.length === 0) {
    // Zona derecha vacía: order mayor que todos los de la izquierda del espacio fijo.
    const allNonFixedProds = prods.filter(p => !_isRealFixedEmptySpace(p));
    if (allNonFixedProds.length === 0) return 1000;
    const maxOrder = Math.max(...allNonFixedProds.map(p => p.order || 0));
    return maxOrder + 1000;
  }

  // Insertar según la X de soltado: ubicar el producto entre el vecino de la izquierda
  // (centro <= drop) y el de la derecha (centro > drop) dentro de la zona.
  const _center = (p: any) => (p.position?.x || 0) + (p.size?.w || 0) / 2;
  let leftNeighbor: any = null;
  let rightNeighbor: any = null;
  for (const p of prodsToRightOfFixedSpace) {
    if (_center(p) <= dropCenterX) {
      leftNeighbor = p;
    } else {
      rightNeighbor = p;
      break;
    }
  }

  if (leftNeighbor && rightNeighbor) return ((leftNeighbor.order || 0) + (rightNeighbor.order || 0)) / 2;
  if (leftNeighbor) return (leftNeighbor.order || 0) + 0.5; // a la derecha de todos los de la zona
  return (rightNeighbor.order || 0) - 0.5; // antes del primero de la zona (pegado al espacio fijo)
};

export const _isRealFixedEmptySpace = prd => {
  if (!prd) return false;
  return !!(
    prd.isFixedEmptySpace ||
    prd.itemData?.isFixedEmptySpace ||
    Number(prd.itemId) === -2 ||
    Number(prd.itemData?.itemId) === -2 ||
    prd.itemData?.code === 'FixedEmptySpace'
  );
};

const _isEmptySpacePlaceholder = (p: any): boolean =>
  !!(
    p?.itemData &&
    (p.itemData.isEmptySpace === true ||
      p.itemData._internalItemId === 'EMPTY_SPACE' ||
      p.itemData.name === 'EMPTY_SPACE' ||
      p.itemData.code === 'EMPTY_SPACE')
  );

const _buildShelfZoneRanges = (
  prods: any[],
  shelfWidth: number,
  marginLeft: number,
  marginRight: number
): Array<{ start: number; end: number; boundedByFixed: boolean }> => {
  const usableStart = marginLeft || 0;
  const usableEnd = shelfWidth - (marginRight || 0);
  const fixedSpaces = (prods || []).filter(p => _isRealFixedEmptySpace(p)).sort((a, b) => (a.fixedX || 0) - (b.fixedX || 0));

  const zones: Array<{ start: number; end: number; boundedByFixed: boolean }> = [];
  let zoneStart = usableStart;
  for (const fs of fixedSpaces) {
    const fsStart = fs.fixedX || 0;
    const fsEnd = fsStart + (fs.fixedWidth || 0);
    zones.push({ start: zoneStart, end: fsStart, boundedByFixed: true });
    zoneStart = fsEnd;
  }
  zones.push({ start: zoneStart, end: usableEnd, boundedByFixed: false });
  return zones;
};

/** Centro X compacto (pre-dispersión). La posición visual dispersa no define la zona. */
const _compactCenterX = (p: any, w: number): number => {
  const pos = p?._originalPosition || p?.position;
  return (pos?.x ?? 0) + w / 2;
};

const _zoneIndexForCenterX = (cx: number, zones: Array<{ start: number; end: number }>): number => {
  for (let z = 0; z < zones.length; z++) {
    if (cx >= zones[z].start - 1 && cx <= zones[z].end + 1) return z;
  }
  let best = 0;
  let minD = Infinity;
  for (let z = 0; z < zones.length; z++) {
    const c = (zones[z].start + zones[z].end) / 2;
    const d = Math.abs(cx - c);
    if (d < minD) {
      minD = d;
      best = z;
    }
  }
  return best;
};

/** CATMAN-B017: prepara un producto clonado para MoveMassProducts — sin position stale del origen. */
export const _prepareProdForMassShelfMove = (prod: any): any => {
  if (!prod) return prod;
  const prepared = _cloneDeep(prod);
  delete prepared.position;
  return prepared;
};

/** CATMAN-B015: orientación al soltar producto en estante (drag & drop entre estantes). */
export const _resolveProdOrientationForAddElement = (prodInShelf: any, data: any) => {
  let rotation = prodInShelf ? prodInShelf.rotation : data?.rotation || 0;
  if (rotation % 90 !== 0) rotation = 0;
  return {
    rotation,
    rotateY: prodInShelf ? !!prodInShelf.rotateY : !!data?.rotateY,
    stacksLyingDown: prodInShelf ? !!prodInShelf.stacksLyingDown : !!data?.stacksLyingDown,
  };
};

export const normalShelfDecorator = _this => {
  // Habilitado nativamente - anteriormente DES-4510: Combinar rotaciones de producto
  setStacksLyingDownFirst(false);

  _this.addElement = async (data, $position) => {
    let prodId = data.id;

    const ALWAYS_NEW_ID = true; //DES-1518
    const newId = ALWAYS_NEW_ID ? _getTmpProdId() : data && data.isList ? _getTmpProdId() : prodId;

    //Si es un item ya insertado
    let prodInShelf = _this.baseObj.findChildById(prodId);

    const { rotation, rotateY, stacksLyingDown } = _resolveProdOrientationForAddElement(prodInShelf, data);

    let limitLoadSize = prodInShelf ? prodInShelf.limitLoadSize : data?.limitLoadSize ?? null;
    let limitStackSize = prodInShelf ? prodInShelf.limitStackSize : data?.limitStackSize ?? null;
    let isPallet = prodInShelf ? prodInShelf.isPallet : !!data?.isPallet;

    if (true) _log('\n\n[addElement (normal)]', { data, $position, prodId, prodInShelf });

    // Ajuste de nueva posicion
    let tmpPosition = { x: $position.x - data.size.w / 2, y: $position.y - data.size.h / 2 };
    const nearNextPosition = _this._aux_getNearNextPosition_x(tmpPosition, data.size.w, data.id);

    // CATMAN-B014: Calcular order considerando espacios fijos
    let calculatedOrder: number;
    const fixedSpaceToLeft = _getFixedSpaceToLeftOfPosition($position.x, _this.prods || []);
    if (fixedSpaceToLeft) {
      // El usuario soltó el producto a la derecha de un espacio fijo: respetar esa intención
      // y además la posición X de soltado para insertarlo en el lugar correcto de la zona.
      calculatedOrder = _getOrderForDropAfterFixedSpace(fixedSpaceToLeft, _this.prods || [], $position.x);
    } else {
      // Comportamiento original: usar el order del producto más cercano
      calculatedOrder = nearNextPosition.obj ? nearNextPosition.obj.order + 0.5 : -0.5;
    }

    const newProd = {
      ...data,
      rotation,
      rotateY,
      stacksLyingDown,
      limitLoadSize,
      limitStackSize,
      isPallet,
      order: calculatedOrder,
      id: newId,
      // CATMAN-B014: Guardar la posición de soltado para que el recálculo detecte la zona
      // (lado del espacio vacío fijo) donde el usuario soltó el producto.
      position: { x: _round(tmpPosition.x), y: _round(tmpPosition.y) },
    };

    // Posición inválida > Restea la posición del drag y aborta
    if (!_this.isValidPos(tmpPosition, data.itemData.size, prodId)) {
      let prodObj = _this.baseObj.findChildById(prodId);
      if (prodObj) prodObj.resetPosition();
      return;
    }

    if (!_this.isValidPromoItem(data, true)) {
      _this.baseObj.genericAlert({
        message: 'El item no se encuentra alcanzado por ninguna de las acciones promocionales asociadas al estante.',
        title: 'Aviso',
        isOnlyWarning: true,
      });

      let prodObj = _this.baseObj.findChildById(prodId);
      if (prodObj) prodObj.resetPosition();
      return;
    }

    // CATMAN-B014: Al colocar/mover un producto con varios frentes (nuevo, desde otro estante, o
    // movido dentro del mismo estante de un lado del espacio vacío fijo al otro), NO se recorta la
    // cantidad de frentes: o entra COMPLETO en la ZONA destino o se rechaza (queda donde estaba).
    // Que entre de un lado del espacio fijo no garantiza que entre del otro.
    if (newProd.facings && newProd.facings >= 1) {
      if (!_this.__hasShelfAvoidCheckSpace()) {
        const sepX = DEFAULT_OFFSET_MIN_PRD_SEP_X;
        // Ancho COMPACTO del producto con TODOS sus frentes (respeta rotación).
        const fullWidth = _calcSizeProd(newProd).w;
        const shelfWidth = Number(_this.size?.w || _this.objDataShelf?.width || 0);
        const dropCenterX = $position.x;
        // Excluir el propio producto (prodId) del cálculo: al moverlo libera su lugar actual.
        const availableInZone = _getAvailableSpaceInZoneAtX(
          dropCenterX,
          _this.prods,
          shelfWidth,
          _this.marginLeft,
          _this.marginRight,
          prodId
        );

        if (fullWidth + sepX > availableInZone) {
          if (isProductCompressionFeatureEnabledFromInjector()) {
            const compressionResult = runShelfCompressionAttempt(_this, {
              changeType: 'add_product',
              newProd: { ...newProd, facings: newProd.facings || 1 },
            });
            if (!compressionResult.ok) {
              notifyCompressionRejected(_this.baseObj._snackBar, _this.baseObj.translate, compressionResult, 'add_product');
              let prodObj = _this.baseObj.findChildById(prodId);
              if (prodObj) prodObj.resetPosition();
              return;
            }
            newProd.facings = compressionResult.updates?.find(u => String(u.prodId) === String(newId))?.facings || newProd.facings;
            await _this.store.dispatch(new AddProdToShelf(newProd, _this.id, prodId)).toPromise();
            dispatchShelfCompressionResult(_this.store, compressionResult);
            if (compressionResult.needsCompression && compressionResult.appliedPercent) {
              notifyCompressionApplied(_this.baseObj._snackBar, _this.baseObj.translate, compressionResult.appliedPercent);
            }
            _this.baseObj.deselectLast();
            await _timeout(0);
            _this.baseObj.selectObjBaseById(newId, _this.getThisScreenOffset());
            return;
          }
          let prodObj = _this.baseObj.findChildById(prodId);
          if (prodObj) prodObj.resetPosition();
          return;
        }
      }
    }

    if (
      isProductCompressionFeatureEnabledFromInjector() &&
      (!prodInShelf || prodInShelf.parentShelf !== _this) &&
      !_this.__hasShelfAvoidCheckSpace() &&
      _this._getMaxRemainingWidthSpace() < (_calcSizeProd({ ...newProd, facings: newProd.facings || 1 }).w || 0) - 0.01
    ) {
      const compressionResult = runShelfCompressionAttempt(_this, {
        changeType: 'add_product',
        newProd: { ...newProd, facings: newProd.facings || 1 },
      });
      if (!compressionResult.ok) {
        notifyCompressionRejected(_this.baseObj._snackBar, _this.baseObj.translate, compressionResult, 'add_product');
        let prodObj = _this.baseObj.findChildById(prodId);
        if (prodObj) prodObj.resetPosition();
        return;
      }
      await _this.store.dispatch(new AddProdToShelf(newProd, _this.id, prodId)).toPromise();
      dispatchShelfCompressionResult(_this.store, compressionResult);
      if (compressionResult.needsCompression && compressionResult.appliedPercent) {
        notifyCompressionApplied(_this.baseObj._snackBar, _this.baseObj.translate, compressionResult.appliedPercent);
      }
      _this.baseObj.deselectLast();
      await _timeout(0);
      _this.baseObj.selectObjBaseById(newId, _this.getThisScreenOffset());
      return;
    }

    // Add
    // La posición de productos en normalShelf es calculada por el store y depende del orden (_auxRecalcProdsPos)
    await _this.store.dispatch(new AddProdToShelf(newProd, _this.id, prodId)).toPromise();

    // Selecciona
    _this.baseObj.deselectLast();
    await _timeout(0);
    _this.baseObj.selectObjBaseById(newId, _this.getThisScreenOffset());
  };

  _this.showGuides = (data, $position) => {
    if (!data || !$position) return;
    const position = { x: $position.x - data.size.w / 2, y: $position.y - data.size.h / 2 };

    const nearNextPosition = _this._aux_getNearNextPosition_x(position, data.size.w, data.id);
    const nearNextPosition_x = nearNextPosition.x;
    const shelfGuideWidth = DEFAULT_NORMAL_SHELF_GUIDE_WIDTH;
    let sepX = DEFAULT_OFFSET_MIN_PRD_SEP_X;

    _this._shelfGuideWarning = !_this.isValidPos(position, data.itemData.size, data.id);

    if (!_this._shelfGuideWarning) {
      _this._shelfGuideWarning = !_this.isValidPromoItem(data, false);
    }

    const rawGuideX = (nearNextPosition_x - sepX * 0.5) * _this.scale - shelfGuideWidth * 0.5;
    const maxGuideX = Math.max(0, _this.size.w * _this.scale - shelfGuideWidth);
    const clampedGuideX = Math.max(0, Math.min(rawGuideX, maxGuideX));
    _this._shelfGuide.position = { x: clampedGuideX, y: 0 };
    _this._shelfGuide.size = { w: shelfGuideWidth, h: _this.size.h * _this.scale };
  };

  _this._aux_getNearNextPosition_x = (position, width, prodId = null) => {
    let sepX = DEFAULT_OFFSET_MIN_PRD_SEP_X;
    if (!_this.prods || !_this.prods.length) return { x: 0 + sepX, obj: null };

    // Busca el {tmpNearObj/tmpNearlastDiff} más cercano
    let tmpNearObj = null;
    let tmpNearlastDiff = 0;
    _this.prods.forEach((obj, i) => {
      let offsetX = obj.position.x + obj.size.w * 0.5 + sepX;
      let diff = position.x + width * 0.5 - offsetX;
      if (diff > 0 && (diff < tmpNearlastDiff || tmpNearlastDiff === 0) && obj.id !== prodId) {
        tmpNearlastDiff = diff;
        tmpNearObj = obj;
      }
    });

    let leftMargin = _this.marginLeft || 0;
    return { x: tmpNearObj ? tmpNearObj.position.x + tmpNearObj.size.w + sepX : leftMargin + sepX, obj: tmpNearObj };
  };

  _this.isValidPos = (position, size, prodId) => {
    let prod = _this.baseObj.findChildById(prodId);
    if (prod && prod.parentShelf === _this) return true;

    if (!position || !size) return false;

    let _tmpSize = _cloneDeep(size);

    if (prod && prod.rotateY) {
      _tmpSize.w = prod.itemData.size.d;
      _tmpSize.d = prod.itemData.size.w;
    }

    if (!_this._isValidHeight(position, _tmpSize, prodId)) return false;
    if (!_this._isValidWidth(position, _tmpSize, prodId)) return false;

    return true;
  };

  _this._isValidHeight = (position, size, prodId = null) => {
    if ((_this as any).__hasShelfAvoidCheckSpace()) return true;

    if (!position || !size) return false;

    // Producto más alto (tiene cuanta el fingerSpace)
    if (size.h + DEFAULT_FINGER_SPACE > _this.size.h) return false;

    let prod = _this.baseObj.findChildById(prodId);
    if (prod?.isPallet === true && size.h + DEFAULT_PALLET_HEIGHT > _this.size.h) return false;

    return true;
  };

  _this._isValidWidth = (position, size, id = null) => {
    if ((_this as any).__hasShelfAvoidCheckSpace()) return true;

    if (!position || !size) return false;
    let sepX = DEFAULT_OFFSET_MIN_PRD_SEP_X;

    const hasFixedSpaces = _this.prods?.some(p => _isRealFixedEmptySpace(p));
    if (hasFixedSpaces) {
      // CATMAN-B014: misma lógica que agregar frentes (`canAddFacings`): validar el espacio libre
      // REAL en la ZONA donde se suelta el producto, respetando el espacio vacío fijo como barrera.
      const shelfWidth = Number(_this.size?.w || _this.objDataShelf?.width || 0);
      const dropCenterX = (position.x || 0) + (size.w || 0) / 2;
      const freeInZone = _getAvailableSpaceInZoneAtX(dropCenterX, _this.prods, shelfWidth, _this.marginLeft, _this.marginRight, id);
      return size.w + sepX <= freeInZone;
    }

    let remaining_space = _this._getMaxRemainingWidthSpace();
    if (size.w + sepX > remaining_space) return false;

    return true;
  };

  _this._getFreeSpaceRatio = () => {
    if (!_this.prods || !_this.prods.length) return 0;

    let shelfWidth = _this.objDataShelf.width;
    let shelfMargins = (_this.marginLeft || 0) + (_this.marginRight || 0);
    let totalShelfWidth = shelfWidth - shelfMargins; //DES-1576

    let remainingWidthSpace = _auxGetMaxRemainingWidthSpace(_this.prods, totalShelfWidth, _this.type);
    let freeSpaceRatio = 1 - remainingWidthSpace / totalShelfWidth;
    return freeSpaceRatio;
  };

  _this.isNewWidthValid = ($newW: number) => {
    let shelfWidth = _this.objDataShelf.width;
    let shelfMargins = (_this.marginLeft || 0) + (_this.marginRight || 0);
    let totalShelfWidth = shelfWidth - shelfMargins; //DES-1576
    let newW = $newW - shelfMargins;

    if (!_this.prods || !_this.prods.length || newW > (_this as any).objDataShelf.width) return true;

    let sepX = DEFAULT_OFFSET_MIN_PRD_SEP_X;

    const newRemainingWidthSpace = _auxGetMaxRemainingWidthSpace(_this.prods, newW, _this.type);
    if (newRemainingWidthSpace < sepX) return false;

    return true;
  };

  //DES-1576
  _this.isNewMarginValid = ($newValue: number, LEFT: boolean = true) => {
    let shelfWidth = _this.objDataShelf.width;
    let shelfMargins = (LEFT ? $newValue || 0 : _this.marginLeft || 0) + (!LEFT ? $newValue || 0 : _this.marginRight || 0);
    let newW = shelfWidth - shelfMargins;

    let otherMargin = LEFT ? _this.marginRight || 0 : _this.marginLeft || 0;
    if ($newValue >= _this.size.w - otherMargin) return false;

    if (!_this.prods || !_this.prods.length || newW > (_this as any).objDataShelf.width) return true;

    let sepX = DEFAULT_OFFSET_MIN_PRD_SEP_X;

    const newRemainingWidthSpace = _auxGetMaxRemainingWidthSpace(_this.prods, newW, _this.type);
    if (newRemainingWidthSpace < sepX) return false;

    return true;
  };

  _this.isNewHeightValid = (newH: number) => {
    if (!_this.prods || !_this.prods.length || newH > (_this as any).objDataShelf.height) return true;

    let rv = true;

    _this.prods.forEach(prod => {
      let _h = prod.itemData.size.h + DEFAULT_FINGER_SPACE + (prod.isPallet ? DEFAULT_PALLET_HEIGHT : 0);
      if (_h > newH) rv = false;
    });

    return rv;
  };

  _this.isNewDepthValid = (newD: number) => {
    if (!_this.prods || !_this.prods.length || newD > (_this as any).objDataShelf.depth) return true;

    let rv = true;

    _this.prods.forEach(prod => {
      let _d = prod.itemData.size.d;
      if (_d > newD) rv = false;
    });

    return rv;
  };
};

// Auxiliar para reacomodar/calcular poscion/tamaño/filtrado de items
const _getProdGroupVisualWidth = (prod: any): number => {
  if (!prod?.groupImgItems?.length) return Number(prod?.size?.w || 0);

  return Math.max(...prod.groupImgItems.map(item => _round(Number(item?.position?.x || 0) + Number(item?.size?.w || 0))), 0);
};

const _getShelfProdsRightEdge = (prods: any[]): number =>
  (prods || []).reduce((maxRight, p) => {
    if (!p || p.isFixedEmptySpace || p.itemData?.isFixedEmptySpace) return maxRight;
    const right = _round(Number(p.position?.x || 0) + _getProdGroupVisualWidth(p));
    return Math.max(maxRight, right);
  }, 0);

export const _recalcPosAndSize_normalShelf = shelf => {
  // Calculo de posicion de estante normal (normalShelf)
  let prods = shelf.prods || [];

  if (isProductCompressionFeatureEnabledFromInjector()) {
    const reconcileMargins = (shelf.marginLeft || 0) + (shelf.marginRight || 0);
    const reconcileWidth = Math.max((shelf.width || 0) - reconcileMargins, 0);
    prods = resolveShelfCompressionForLayout(prods, reconcileWidth, shelf.type);
    shelf = { ...shelf, prods };
  }

  // Ordena y filtra nulos
  prods = _orderBy(
    prods.filter(obj => !!(obj && obj.itemData && obj.itemData._internalItemId)),
    ['order']
  );

  if (!shelf.width) {
    _warn('NO shelf.width', shelf);
    return;
  }

  let shelfWidth = shelf.width;
  let shelfMargins = (shelf.marginLeft || 0) + (shelf.marginRight || 0);
  let totalShelfWidth = shelfWidth - shelfMargins; //DES-1576

  let remainingWidthSpace = _auxGetMaxRemainingWidthSpace(prods, totalShelfWidth, shelf.type);
  let freeSpaceRatio = 1 - remainingWidthSpace / totalShelfWidth;

  let sepX = DEFAULT_OFFSET_MIN_PRD_SEP_X;
  let prodsLength = prods.length;
  let marginLeftOffset = shelf.marginLeft || 0; //DES-1576

  // AUTOESPACIADO / Si la ocupación es mayor a 75% (DEFAULT_MIN_RATIO_FOR_AUTOSPACE) autoAjusta el espaciado
  if (DEFAULT_AUTOSPACE && prodsLength && freeSpaceRatio > DEFAULT_MIN_RATIO_FOR_AUTOSPACE) {
    sepX = _round((remainingWidthSpace + prodsLength * DEFAULT_OFFSET_MIN_PRD_SEP_X) / (prodsLength + 1));
    sepX = Math.max(DEFAULT_OFFSET_MIN_PRD_SEP_X, sepX);
  }

  if (shelf?.__shelfAvoidCheckSpace === true && remainingWidthSpace < 0) {
    sepX = _round(remainingWidthSpace / (prodsLength - 1));
  }

  let canDeleteNextProd = false; /*2k5pbp no agrupa para poder rotar independientemente*/
  let hasDeleteNextProd = false;
  if (canDeleteNextProd) {
    // Se fija si hay agrupaciones
    prods = prods.map((obj, i) => {
      if (hasDeleteNextProd) {
        hasDeleteNextProd = false;
        return null;
      }

      let nextProd = prods[i + 1];
      if (nextProd && nextProd.itemData._internalItemId === obj.itemData._internalItemId) {
        let totalFacings = (nextProd.facings || 1) + (obj.facings || 1);
        hasDeleteNextProd = true; // Marca el próximo para borrado
        return { ...obj, id: nextProd.id, facings: totalFacings };
      }

      return obj;
    });
  }

  // Separar productos fijos de los normales
  const fixedEmptySpaces: any[] = [];
  const normalProds = [];

  prods.forEach(obj => {
    if (obj && (obj.isFixedEmptySpace || obj.itemData?.isFixedEmptySpace)) {
      fixedEmptySpaces.push(obj);
    } else {
      normalProds.push(obj);
    }
  });

  // Ordenar espacios fijos por posición para el cálculo de "salto"
  fixedEmptySpaces.sort((a, b) => (a.fixedX || 0) - (b.fixedX || 0));

  // Calcular tamaño de cada producto normal (la posición se asigna luego, según zona)
  let sizedNormalProds = normalProds.map(($obj, i) => {
    if (!$obj || !$obj.itemData) return $obj;

    let obj = _cloneDeep({ ...$obj, itemData: null });
    obj.itemData = $obj.itemData;

    obj.stacksLyingDown = !!obj.stacksLyingDown;
    obj.stacks = _auxGetmaxStacks(obj, shelf.height, obj.stacksLyingDown, obj.rotation);

    const subItemsAndTotalSize = _auxCalcGroupImgItemsAndTotalSize(obj, shelf);
    obj.order = i;
    obj.groupImgItems = subItemsAndTotalSize.items;
    obj.size = subItemsAndTotalSize.totalSize;
    obj.imgSize = subItemsAndTotalSize.imgSize;
    obj.$totalItems = (subItemsAndTotalSize as any).limitLoadSize || subItemsAndTotalSize.$totalItems || 0;
    obj.$totalItemsMax = subItemsAndTotalSize.$totalItemsMax || 0;
    obj.$parenShelf = shelf.id;

    // Centro previo (última ubicación conocida / posición de soltado) para asignar zona.
    // Usar el CENTRO (no el borde izq.) hace que al insertar un espacio fijo los productos se
    // separen hacia el lado correcto en vez de quedar por encima del espacio vacío.
    if ($obj.position && typeof $obj.position.x === 'number') {
      const prevW = Number($obj.size?.w || obj.size?.w || 0);
      obj._prevCenterX = $obj.position.x + prevW / 2;
    } else {
      obj._prevCenterX = null;
    }
    return obj;
  });

  let processedNormalProds;

  if (fixedEmptySpaces.length === 0) {
    // Sin espacios fijos: colocación secuencial clásica
    let _lastProd = null;
    processedNormalProds = sizedNormalProds.map(obj => {
      if (!obj || !obj.itemData) return obj;
      let x = Math.max(_round((_lastProd ? _lastProd.position.x + _lastProd.size.w : marginLeftOffset) + sepX), marginLeftOffset);
      obj.position = { x: Math.max(_round(x), marginLeftOffset), y: _round(shelf.height - obj.size.h) };
      delete obj._prevCenterX;
      _lastProd = obj;
      return obj;
    });
  } else {
    // CATMAN-B014: Colocación por ZONAS. Un producto NUNCA cruza automáticamente un espacio fijo.
    // Las zonas son los tramos entre el margen izquierdo, los espacios fijos y el margen derecho.
    const usableEnd = shelfWidth - (shelf.marginRight || 0);
    const zoneRanges: Array<{ start: number; end: number }> = [];
    let zStart = marginLeftOffset;
    for (const fs of fixedEmptySpaces) {
      const fsStart = fs.fixedX || 0;
      const fsEnd = fsStart + (fs.fixedWidth || 0);
      zoneRanges.push({ start: zStart, end: fsStart });
      zStart = fsEnd;
    }
    zoneRanges.push({ start: zStart, end: usableEnd });

    const _zoneIndexForX = (x: number | null): number | null => {
      if (x == null) return null;
      for (let z = 0; z < zoneRanges.length; z++) {
        if (x >= zoneRanges[z].start - 1 && x < zoneRanges[z].end + 1) return z;
      }
      let best = 0;
      let minD = Infinity;
      for (let z = 0; z < zoneRanges.length; z++) {
        const c = (zoneRanges[z].start + zoneRanges[z].end) / 2;
        const d = Math.abs(x - c);
        if (d < minD) {
          minD = d;
          best = z;
        }
      }
      return best;
    };

    // Asignar zona: por centro previo si existe; si no (producto nuevo sin posición), hereda del vecino en orden
    const withZone = sizedNormalProds.map(obj => ({
      obj,
      zone: obj && obj.itemData ? _zoneIndexForX(obj._prevCenterX) : null,
    }));
    const someKnown = withZone.some(w => w.zone != null);

    if (!someKnown) {
      // Carga inicial sin posiciones: colocación secuencial saltando los espacios fijos (layout determinista por orden)
      const fixedRanges: FixedSpaceRange[] = fixedEmptySpaces.map(fs => ({
        start: fs.fixedX || 0,
        end: (fs.fixedX || 0) + (fs.fixedWidth || 0),
      }));
      let _lastProd = null;
      processedNormalProds = sizedNormalProds.map(obj => {
        if (!obj || !obj.itemData) return obj;
        let x = Math.max(_round((_lastProd ? _lastProd.position.x + _lastProd.size.w : marginLeftOffset) + sepX), marginLeftOffset);
        x = resolveXAfterFixedSpaces(x, obj.size.w, fixedRanges, sepX, DEFAULT_OFFSET_MIN_PRD_SEP_X);
        obj.position = { x: Math.max(_round(x), marginLeftOffset), y: _round(shelf.height - obj.size.h) };
        delete obj._prevCenterX;
        _lastProd = obj;
        return obj;
      });
    } else {
      // Propagar zona a los nuevos (sin centro previo) desde el vecino más cercano en orden
      for (let i = 0; i < withZone.length; i++) {
        if (withZone[i].obj && withZone[i].obj.itemData && withZone[i].zone == null) {
          let z: number | null = null;
          for (let k = i - 1; k >= 0; k--) {
            if (withZone[k].zone != null) {
              z = withZone[k].zone;
              break;
            }
          }
          if (z == null) {
            for (let k = i + 1; k < withZone.length; k++) {
              if (withZone[k].zone != null) {
                z = withZone[k].zone;
                break;
              }
            }
          }
          withZone[i].zone = z != null ? z : 0;
        }
      }

      // Colocar secuencialmente dentro de cada zona (respetando el orden), sin cruzar espacios fijos.
      // El producto se inserta en el slot correcto vía su `order` (calculado según la X de soltado en
      // `addElement`), por lo que empaquetar la zona lo deja entre sus vecinos y, al borrar, los
      // productos se reagrupan cerrando el hueco.
      // Nunca usar separación negativa: en modo "no validar tamaños" los productos sobresalen
      // del estante (última zona) en vez de comprimirse/superponerse.
      const zoneSepX = sepX < DEFAULT_OFFSET_MIN_PRD_SEP_X ? DEFAULT_OFFSET_MIN_PRD_SEP_X : sepX;
      const cursorByZone = zoneRanges.map(z => z.start);
      processedNormalProds = withZone.map(({ obj, zone }) => {
        if (!obj || !obj.itemData) return obj;
        const zIdx = zone == null ? 0 : zone;
        const range = zoneRanges[zIdx] || zoneRanges[zoneRanges.length - 1];
        let x = cursorByZone[zIdx] === range.start ? range.start + zoneSepX : _round(cursorByZone[zIdx] + zoneSepX);
        obj.position = { x: _round(x), y: _round(shelf.height - obj.size.h) };
        cursorByZone[zIdx] = x + obj.size.w;
        delete obj._prevCenterX;
        return obj;
      });
    }
  }

  // Calcular posición de espacios fijos
  let processedFixedSpaces = fixedEmptySpaces.map(($obj, i) => {
    if (!$obj || !$obj.itemData) return $obj;

    let obj = _cloneDeep({ ...$obj, itemData: null });
    obj.itemData = $obj.itemData;

    obj.stacksLyingDown = false;
    obj.stacks = 1;

    const fixedW = obj.fixedWidth || obj.facings || 1;
    obj.facings = Math.round(fixedW);

    const subItemsAndTotalSize = _auxCalcGroupImgItemsAndTotalSize(obj, shelf);
    obj.order = 9000 + i;
    obj.groupImgItems = subItemsAndTotalSize.items;
    obj.size = { ...subItemsAndTotalSize.totalSize, w: fixedW };
    obj.imgSize = subItemsAndTotalSize.imgSize;
    obj.$totalItems = 0;
    obj.$totalItemsMax = 0;
    obj.$parenShelf = shelf.id;

    obj.position = {
      x: _round(obj.fixedX || 0),
      y: _round(shelf.height - obj.size.h),
    };

    // Recalcular groupImgItems para que cubra el ancho fijo
    if (obj.groupImgItems && obj.groupImgItems.length > 0) {
      obj.groupImgItems = [{ ...obj.groupImgItems[0], size: { ...obj.size } }];
    }

    return obj;
  });

  prods = [...processedNormalProds, ...processedFixedSpaces];

  // DISPERSIÓN / Si está activada la dispersión, elimina espacios y distribuye uniformemente
  // Aplicar dispersión si hay más de un producto O si hay un producto con múltiples facings
  const hasMultipleProducts = prodsLength > 1;
  const hasSingleProductWithMultipleFacings = prodsLength === 1 && prods[0] && (prods[0].facings || 1) > 1;

  const compressionActive = isProductCompressionFeatureEnabledFromInjector() && hasShelfActiveCompression(prods);

  // Con compresión activa no dispersar: los frentes deben verse apretados/comprimidos, no separados.
  if (GLOBAL_JUSTIFY_FACINGS && !compressionActive && (hasMultipleProducts || hasSingleProductWithMultipleFacings)) {
    prods = _disperseProducts(prods, totalShelfWidth, marginLeftOffset, shelf.height, false);
  }

  const shelfEnd = marginLeftOffset + totalShelfWidth;
  const hasVisualOverflow = _getShelfProdsRightEdge(prods) > shelfEnd + 0.05;

  if (compressionActive || (!GLOBAL_JUSTIFY_FACINGS && hasVisualOverflow)) {
    prods = _fitCompressedProdsVisualToShelf(prods, marginLeftOffset, totalShelfWidth, shelf.height);
  }

  return prods || [];
};

/** Escala frentes para llenar el estante sin desbordar (CATMAN-S54). */
export const _fitCompressedProdsVisualToShelf = (prods, marginLeftOffset, totalShelfWidth, shelfHeight) => {
  if (!prods?.length) return prods;

  const isFixed = p => !!(p?.isFixedEmptySpace || p?.itemData?.isFixedEmptySpace);
  const normalProds = prods.filter(p => p && !isFixed(p));
  if (!normalProds.length) return prods;

  const totalUsed = normalProds.reduce((sum, p) => sum + _getProdGroupVisualWidth(p), 0);
  if (!(totalUsed > 0)) return prods;

  const scale = totalShelfWidth / totalUsed;

  const rescaleProd = (p: any, widthScale = scale) => {
    const facings = Math.max(1, Number(p.facings) || 1);
    const baseVisualW = _getProdGroupVisualWidth(p);
    const newTotalW = _round(baseVisualW * widthScale);
    const perFacingW = facings > 0 ? newTotalW / facings : newTotalW;

    let groupImgItems = p.groupImgItems;
    if (groupImgItems?.length) {
      groupImgItems = groupImgItems.map(item => {
        const facingIndex = item?.facingIndex != null ? item.facingIndex : 0;
        const inFacingX = _round((item?.inFacingX != null ? item.inFacingX : 0) * widthScale);
        const itemW = item?.size?.w != null ? _round(Number(item.size.w) * widthScale) : _round(perFacingW);
        return {
          ...item,
          size: { ...item.size, w: itemW },
          position: { x: _round(facingIndex * perFacingW + inFacingX), y: item.position?.y ?? 0 },
        };
      });
    }

    return { ...p, size: { ...p.size, w: newTotalW }, groupImgItems };
  };

  let x = marginLeftOffset;
  // CATMAN-B019: indexar por REFERENCIA de objeto, no por `id`. En el preview de templates
  // el backend envía `id: 0` para todos los productos del estante, por lo que `_PRD-0` colisiona
  // entre ellos; un Map por id hacía que el último producto pisara al primero (el primero
  // desaparecía y se duplicaba el último). La referencia siempre es única.
  const byRef = new Map<any, any>();

  normalProds.forEach((p, index) => {
    let rescaled = rescaleProd(p);
    if (index === normalProds.length - 1) {
      const usedBefore = x - marginLeftOffset;
      const remaining = _round(totalShelfWidth - usedBefore);
      if (remaining > 0 && Math.abs(remaining - Number(rescaled.size?.w || 0)) > 0.01) {
        const lastScale = remaining / Math.max(_getProdGroupVisualWidth(p), 0.01);
        rescaled = rescaleProd(p, lastScale);
        rescaled.size = { ...rescaled.size, w: remaining };
      }
    }

    byRef.set(p, {
      ...rescaled,
      position: { x: _round(x), y: _round(shelfHeight - (rescaled.size?.h || 0)) },
    });
    x += Number(rescaled.size?.w || 0);
  });

  return prods.map(p => (isFixed(p) ? p : byRef.get(p) || p));
};

export const _getShelfLayoutRightEdge = (prods: any[], marginLeftOffset = 0, totalShelfWidth = 0) => ({
  rightEdge: _getShelfProdsRightEdge(prods),
  shelfEnd: marginLeftOffset + totalShelfWidth,
});

/**
 * @param prods Array de productos ya calculados
 * @param totalShelfWidth Ancho total disponible del estante
 * @param marginLeftOffset Margen izquierdo del estante
 * @param shelfHeight Altura del estante
 * @returns Array de productos con posiciones recalculadas
 */
const _disperseProducts = (prods, totalShelfWidth, marginLeftOffset, shelfHeight, forceFillShelf = false) => {
  if (!prods || prods.length === 0) return prods;

  // Si hay un solo producto, solo aplicar dispersión si tiene múltiples facings
  if (prods.length === 1) {
    const singleProd = prods[0];
    if (!singleProd || (singleProd.facings || 1) <= 1) {
      return prods;
    }
  }

  // Guardamos las posiciones originales si no las teníamos
  if (!prods.some(p => p._originalPosition)) {
    prods.forEach(p => {
      if (p.position) {
        p._originalPosition = { ...p.position };
      }
    });
  }

  // Si el dispersar está desactivado, retornamos los productos con sus posiciones originales
  if (!GLOBAL_JUSTIFY_FACINGS) {
    return prods.map(p => {
      if (p._originalPosition) {
        return { ...p, position: { ...p._originalPosition } };
      }
      return p;
    });
  }

  // SEPARAR PRODUCTOS REALES, ESPACIOS VACÍOS NORMALES Y ESPACIOS FIJOS
  const realProds = [];
  const emptySpaces = [];
  const fixedEmptySpaces = [];

  prods.forEach(prod => {
    const isFixed = prod.isFixedEmptySpace || prod.itemData?.isFixedEmptySpace;
    if (isFixed) {
      fixedEmptySpaces.push({ ...prod });
      return;
    }

    const isEmpty =
      prod.itemData &&
      (prod.itemData.isEmptySpace === true ||
        prod.itemData._internalItemId === 'EMPTY_SPACE' ||
        prod.itemData.name === 'EMPTY_SPACE' ||
        prod.itemData.code === 'EMPTY_SPACE');

    if (isEmpty) {
      emptySpaces.push({ ...prod, position: { x: -9999, y: -9999 } });
    } else {
      realProds.push({ ...prod });
    }
  });

  if (realProds.length === 0) {
    return [...fixedEmptySpaces, ...emptySpaces];
  }

  // Sin espacios fijos: dispersión global clásica
  if (fixedEmptySpaces.length === 0) {
    return _disperseInZone(realProds, marginLeftOffset, marginLeftOffset + totalShelfWidth, shelfHeight, emptySpaces, forceFillShelf);
  }

  // CON ESPACIOS FIJOS: dispersión por zonas
  fixedEmptySpaces.sort((a, b) => (a.fixedX || 0) - (b.fixedX || 0));
  const shelfEnd = marginLeftOffset + totalShelfWidth;

  const zones = [];
  let zoneStart = marginLeftOffset;
  for (const fs of fixedEmptySpaces) {
    const fsStart = fs.fixedX || 0;
    const fsEnd = fsStart + (fs.fixedWidth || 0);
    if (fsStart > zoneStart) zones.push({ start: zoneStart, end: fsStart });
    zoneStart = fsEnd;
  }
  if (zoneStart < shelfEnd) zones.push({ start: zoneStart, end: shelfEnd });

  const zoneProds = zones.map(() => []);
  realProds.forEach(prod => {
    const prodX = prod.position?.x ?? 0;
    let assigned = false;
    for (let z = 0; z < zones.length; z++) {
      if (prodX >= zones[z].start - 1 && prodX + prod.size.w <= zones[z].end + 1) {
        zoneProds[z].push(prod);
        assigned = true;
        break;
      }
    }
    if (!assigned) {
      const prodCenter = prodX + prod.size.w / 2;
      let minDist = Infinity,
        bestZone = 0;
      for (let z = 0; z < zones.length; z++) {
        const d = Math.abs(prodCenter - (zones[z].start + zones[z].end) / 2);
        if (d < minDist) {
          minDist = d;
          bestZone = z;
        }
      }
      zoneProds[bestZone].push(prod);
    }
  });

  const dispersedProds = [];
  for (let z = 0; z < zones.length; z++) {
    if (zoneProds[z].length === 0) continue;
    dispersedProds.push(..._disperseInZone(zoneProds[z], zones[z].start, zones[z].end, shelfHeight, [], forceFillShelf));
  }

  return [...dispersedProds, ...fixedEmptySpaces, ...emptySpaces];
};

const _disperseInZone = (prodsInZone, zoneStart, zoneEnd, shelfHeight, passThruEmpty, forceFillShelf = false) => {
  if (!prodsInZone || prodsInZone.length === 0) return [...passThruEmpty];
  const zoneWidth = zoneEnd - zoneStart;
  let totalProductsWidth = 0,
    totalFacings = 0;
  prodsInZone.forEach(prod => {
    if (prod && prod.size) totalProductsWidth += prod.size.w;
    totalFacings += prod.facings || 1;
  });
  const availableSpace = zoneWidth - totalProductsWidth;
  if (availableSpace <= 0) return [...prodsInZone, ...passThruEmpty];

  const freeSpaceRatio = zoneWidth > 0 ? 1 - totalProductsWidth / zoneWidth : 1;
  const shouldApply =
    (forceFillShelf && availableSpace > 0) ||
    (DEFAULT_PRODUCT_INTERNAL_AUTOSPACE && freeSpaceRatio < 1 - DEFAULT_MIN_RATIO_FOR_PRODUCT_INTERNAL_AUTOSPACE);
  if (!shouldApply) return [...prodsInZone, ...passThruEmpty];
  let uniformSpacing = availableSpace / (totalFacings + 1);
  uniformSpacing = Math.max(DEFAULT_OFFSET_MIN_PRD_SEP_X, uniformSpacing);
  let currentX = zoneStart + uniformSpacing;
  const result = [];
  prodsInZone.forEach(originalProd => {
    const facings = originalProd.facings || 1;
    const individualFacingWidth = originalProd.size.w / facings;
    const newProd = { ...originalProd };
    newProd.position = { x: _round(currentX), y: _round(shelfHeight - originalProd.size.h) };
    if (newProd.groupImgItems && newProd.groupImgItems.length > 0) {
      newProd.groupImgItems = newProd.groupImgItems.map((item, index) => {
        const facingIndex = item?.facingIndex != null ? item.facingIndex : Math.floor(index / (originalProd.stacks || 1));
        const inFacingX = item?.inFacingX != null ? item.inFacingX : 0;
        return { ...item, position: { x: facingIndex * (individualFacingWidth + uniformSpacing) + inFacingX, y: item.position.y } };
      });
      let newTotalWidth = (facings - 1) * (individualFacingWidth + uniformSpacing) + individualFacingWidth;
      const maxWidthInZone = Math.max(0, zoneEnd - currentX);
      newTotalWidth = Math.min(newTotalWidth, maxWidthInZone);
      newProd.size = { ...newProd.size, w: _round(newTotalWidth) };
    }
    if (newProd.position.x + newProd.size.w > zoneEnd + 0.01) {
      newProd.position.x = Math.max(zoneStart, zoneEnd - newProd.size.w);
    }
    currentX += newProd.size.w + uniformSpacing;
    result.push(newProd);
  });
  return [...result, ...passThruEmpty];
};

const _getSrpLoadMultiplier = (prod: any): number => {
  const activeTypeId = _resolveActiveExhibitionTypeId(prod);
  if (activeTypeId != null && activeTypeId !== EXHIBITION_TYPE_SRP) return 1;

  const srpFormat = _getExhibitionFormatByTypeId(prod, EXHIBITION_TYPE_SRP);
  if (!srpFormat) return 1;

  const depthLoad = Number(srpFormat?.depthLoad) || 1;
  const frontLoad = Number(srpFormat?.frontLoad) || 1;
  const mult = depthLoad * frontLoad;
  return mult > 1 ? mult : 1;
};

const _getSrpIndividualImageSrc = (prod: any): string | null => {
  const itemData = prod?.itemData;
  const individualFormat = _getExhibitionFormatByTypeId(prod, 1);
  return itemData?._individualImagePlanogramFront || individualFormat?.imagePlanogramFront || itemData?.imagePlanogramFront || null;
};

const _getSrpHeightLoad = (prod: any): number => {
  const activeTypeId = _resolveActiveExhibitionTypeId(prod);
  if (activeTypeId != null && activeTypeId !== EXHIBITION_TYPE_SRP) return 1;

  const srpFormat = _getExhibitionFormatByTypeId(prod, EXHIBITION_TYPE_SRP);
  if (!srpFormat) return 1;

  return Math.max(1, Number(srpFormat?.heightLoad) || 1);
};

// SRP fallback: si SRP no tiene imagen, tilea la imagen individual según frontLoad y/o heightLoad
const _getSrpNoImageTileConfig = (prod: any): { frontLoad: number; heightLoad: number; individualSrc: string } | null => {
  const activeTypeId = _resolveActiveExhibitionTypeId(prod);
  if (activeTypeId != null && activeTypeId !== EXHIBITION_TYPE_SRP) return null;

  const srpFormat = _getExhibitionFormatByTypeId(prod, EXHIBITION_TYPE_SRP);
  if (!srpFormat) return null;

  if (srpFormat?.imagePlanogramFront) return null;

  const frontLoad = Number(srpFormat?.frontLoad) || 1;
  const heightLoad = _getSrpHeightLoad(prod);
  if (frontLoad < 2 && heightLoad < 2) return null;

  const individualSrc = _getSrpIndividualImageSrc(prod);
  if (!individualSrc) return null;

  return { frontLoad, heightLoad, individualSrc };
};

interface _GroupImgCellParams {
  rvItems: any[];
  facingIndex: number;
  cellId: string;
  x: number;
  y: number;
  w: number;
  h: number;
  imageSrc: string | null;
  isLyingDown: boolean;
  forcedImg: boolean;
  canTileSrp: boolean;
  srpNoImageTileConfig: { frontLoad: number; heightLoad: number; individualSrc: string } | null;
  srpHeightLoad: number;
}

// CATMAN-B010: apilado vertical SRP — N imágenes con altura real en lugar de una imagen estirada
const _pushGroupImgItemsForCell = (params: _GroupImgCellParams) => {
  const { rvItems, facingIndex, cellId, x, y, w, h, imageSrc, isLyingDown, forcedImg, canTileSrp, srpNoImageTileConfig, srpHeightLoad } =
    params;

  if (srpNoImageTileConfig && canTileSrp) {
    const tileW = w / srpNoImageTileConfig.frontLoad;
    const tileH = h / srpNoImageTileConfig.heightLoad;
    for (let v = 0; v < srpNoImageTileConfig.heightLoad; v++) {
      for (let k = 0; k < srpNoImageTileConfig.frontLoad; k++) {
        rvItems.push({
          imageSrc: srpNoImageTileConfig.individualSrc,
          size: { w: tileW, h: tileH },
          position: { x: _round(x + tileW * k), y: _round(y + tileH * v) },
          id: `${cellId}-${v}-${k}`,
          facingIndex,
          inFacingX: _round(tileW * k),
          isLyingDown: false,
          forcedImg: false,
        });
      }
    }
    return;
  }

  if (canTileSrp && srpHeightLoad > 1) {
    const tileH = h / srpHeightLoad;
    for (let v = 0; v < srpHeightLoad; v++) {
      rvItems.push({
        imageSrc: imageSrc || null,
        size: { w, h: tileH },
        position: { x, y: _round(y + tileH * v) },
        id: `${cellId}-${v}`,
        facingIndex,
        inFacingX: 0,
        isLyingDown: false,
        forcedImg,
      });
    }
    return;
  }

  rvItems.push({
    imageSrc: imageSrc || null,
    size: { w, h },
    position: { x, y },
    id: cellId,
    facingIndex,
    inFacingX: 0,
    isLyingDown,
    forcedImg,
  });
};

//Patch Multimples rotaciones DES-4510
export const _auxCalcGroupImgItemsAndTotalSizeMultipleRotations = ($prod, shelf = null) => {
  const prod = $prod?.itemData?.size
    ? $prod
    : { ...$prod, itemData: $prod?.itemData || { ...$prod?.itemData, size: $prod?.itemData?.size || {} } };

  const sepX = DEFAULT_OFFSET_MIN_PRD_SEP_X;

  let stacks = prod.stacks || 1;
  let facings = prod.facings || 1;

  //Limite de carga (no puede haber más facings que carga limitada)
  if (prod.limitLoadSize && facings * stacks > prod.limitLoadSize) stacks = Math.min(Math.floor(prod.limitLoadSize / facings) || 1, stacks);
  if (prod.limitLoadSize && facings * stacks > prod.limitLoadSize) facings = Math.min(prod.limitLoadSize, facings);

  const defaultImage = (prod.itemData || {}).imagePlanogramFront;

  const lyingImage = (prod.itemData || {}).imagePlanogramTop;

  const stacksLyingDown = prod.stacksLyingDown; /*Rotación en X*/

  const hasRotation = prod.rotation; /*Rotación en Z*/

  const isRotateY = prod.rotateY; /*Rotación en Y*/

  const rotateYImage = (prod.itemData || {}).imagePlanogramLateral;

  const srpNoImageTileConfig = _getSrpNoImageTileConfig(prod);
  const srpHeightLoad = _getSrpHeightLoad(prod);

  let tmpWidth = prod.itemData.size.w;
  let tmpHeight = prod.itemData.size.h;
  let tmpDepth = prod.itemData.size.d || prod.itemData.size.w;
  let imgSize = { w: 0, h: 0, originX: 0, originY: 0, offsetX: 0, offsetY: 0 };

  let shelfDepth = (shelf || {}).depth;

  //Si viene el shelf como parámetro calcula los items que entran en total
  let $totalItems = shelfDepth ? 0 : null;
  let $totalItemsMax = shelfDepth ? 0 : null;

  if (hasRotation) {
    if (stacksLyingDown) {
      tmpWidth = prod.itemData.size.w;
      tmpHeight = tmpDepth;
    }

    if (isRotateY) {
      tmpWidth = tmpDepth;
      tmpHeight = prod.itemData.size.h;
    }

    //Si está rotado calcula de nuevo el width y height y completa valores para imgSize
    const totalBound = _calcRotateTransforms(tmpWidth, tmpHeight, prod.rotation || 0);

    imgSize = {
      ...totalBound,
      w: tmpWidth,
      h: tmpHeight,
    };

    tmpWidth = totalBound.w;
    tmpHeight = totalBound.h;

    if (stacksLyingDown) {
      tmpDepth = totalBound.h;
    }
    if (isRotateY) {
      tmpDepth = totalBound.w;
    }
  }

  const rvItems = [];

  // Calcula subitems
  let _accWidth = 0; /* Va acumulando el width total */
  let _accHeight = 0; /* Va acumulando el height total */
  for (let i = 0; i < facings; i++) {
    const facingBaseX = _round(_accWidth);
    _accHeight = 0; // Por cada columna nueva resetea el total del height
    for (let j = 0; j < stacks; j++) {
      const _id = `${prod.id}${i}${j}`;
      let _img = defaultImage ? defaultImage : null;
      let _w = tmpWidth;
      let _h = tmpHeight;

      let _isLyingDown = stacksLyingDown && j > 0; // Solo están voltados los de abajo
      if (!_stacksLyingDownFirst) _isLyingDown = stacksLyingDown && true; // Todos están voltados los de abajo //DES-4510

      let _forcedImg = false;

      if (_isLyingDown) {
        // Si está tumbado la altura es la profundidad
        _h = tmpDepth;
        if (lyingImage) {
          _img = lyingImage; // Si tiene la imagen la usa
        } else {
          _forcedImg = true; // Marca la imagen como forzada
        }

        // va sumando la cantidad de items que entran en profundidad
        let itemsInDepth = Math.floor(shelfDepth / prod.itemData.size.h) || 1;

        // Si es SRP, multiplicar por depthLoad * frontLoad (exhibitionFormats o unitOfExhibition)
        itemsInDepth *= _getSrpLoadMultiplier(prod);

        if (shelfDepth) $totalItems += itemsInDepth;
      } else if (isRotateY) {
        // Rotado en Y
        _w = tmpDepth;
        _img = rotateYImage;

        // va sumando la cantidad de items que entran en profundidad
        let itemsInDepth = Math.floor(shelfDepth / prod.itemData.size.w) || 1;

        // Si es SRP, multiplicar por depthLoad * frontLoad (exhibitionFormats o unitOfExhibition)
        itemsInDepth *= _getSrpLoadMultiplier(prod);

        if (shelfDepth) $totalItems += itemsInDepth;
      } else {
        // va sumando la cantidad de items que entran en profundidad
        let itemsInDepth = Math.floor(shelfDepth / (prod.itemData.size.d || prod.itemData.size.w)) || 1;

        // Si es SRP, multiplicar por depthLoad * frontLoad (exhibitionFormats o unitOfExhibition)
        itemsInDepth *= _getSrpLoadMultiplier(prod);

        if (shelfDepth) $totalItems += itemsInDepth;
      }

      _w = _round(applyCompressionToFacingWidth(_w, prod));

      const _x = facingBaseX;
      if (j === stacks - 1) _accWidth += _w + sepX; // Acumula el ancho solo con el último producto de la fila

      const _y = _round(_accHeight);
      _accHeight += _h;

      _pushGroupImgItemsForCell({
        rvItems,
        facingIndex: i,
        cellId: _id,
        x: _x,
        y: _y,
        w: _w,
        h: _h,
        imageSrc: _img,
        isLyingDown: _isLyingDown,
        forcedImg: _forcedImg,
        canTileSrp: !hasRotation && !isRotateY && !_isLyingDown,
        srpNoImageTileConfig,
        srpHeightLoad,
      });
    }
  }

  //Limite de carga
  if (prod.limitLoadSize && $totalItems > prod.limitLoadSize) $totalItems = prod.limitLoadSize;

  $totalItemsMax = $totalItems;

  let unMed = _toNumber(prod.itemData.conversion || prod.itemData.unMed || 1);

  //DES-3945 -> Unidad de medida mayor a 1
  const TOTAL_ITEMS_WHITH_CONVERSION_RATIO = true;
  if (TOTAL_ITEMS_WHITH_CONVERSION_RATIO && unMed != null && unMed > 1) {
    $totalItems *= unMed;
    $totalItemsMax *= unMed;
  }

  const totalSize = { w: _round(_accWidth - sepX), h: _round(_accHeight) };

  return { items: rvItems, totalSize, imgSize, $totalItems, $totalItemsMax, __auxCalcGroup: true };
};

/*
    Calcula grupo de imagenes y posición total para productos de normalShelf
    Es el único lugar donde deberian hacerse estos calculos
    Tiene en cuenta rotaciones / apilado y múltiples frentes
*/
export const _auxCalcGroupImgItemsAndTotalSize = ($prod, shelf = null) => {
  //DES-4510 Permitir rotaciones anidadads
  if (true && $prod.rotation && ($prod.rotateY || $prod.stacksLyingDown)) {
    return _auxCalcGroupImgItemsAndTotalSizeMultipleRotations($prod, shelf);
  }

  const prod = $prod?.itemData?.size
    ? $prod
    : { ...$prod, itemData: $prod?.itemData || { ...$prod?.itemData, size: $prod?.itemData?.size || {} } };

  const sepX = DEFAULT_OFFSET_MIN_PRD_SEP_X;

  let stacks = prod.stacks || 1;
  let facings = prod.facings || 1;

  //Limite de carga (no puede haber más facings que carga limitada)
  if (prod.limitLoadSize && facings * stacks > prod.limitLoadSize) stacks = Math.min(Math.floor(prod.limitLoadSize / facings) || 1, stacks);
  if (prod.limitLoadSize && facings * stacks > prod.limitLoadSize) facings = Math.min(prod.limitLoadSize, facings);

  const defaultImage = (prod.itemData || {}).imagePlanogramFront;
  const lyingImage = (prod.itemData || {}).imagePlanogramTop;
  const stacksLyingDown = prod.stacksLyingDown; /*Rotación en X*/
  const hasRotation = prod.rotation; /*Rotación en Z*/

  const isRotateY = prod.rotateY; /*Rotación en Y*/
  const rotateYImage = (prod.itemData || {}).imagePlanogramLateral;

  const srpNoImageTileConfig = _getSrpNoImageTileConfig(prod);
  const srpHeightLoad = _getSrpHeightLoad(prod);

  let tmpWidth = prod.itemData.size.w;
  let tmpHeight = prod.itemData.size.h;
  let tmpDepth = prod.itemData.size.d || prod.itemData.size.w;
  let imgSize = { w: 0, h: 0, originX: 0, originY: 0, offsetX: 0, offsetY: 0 };

  let shelfDepth = (shelf || {}).depth;

  //Si viene el shelf como parámetro calcula los items que entran en total
  let $totalItems = shelfDepth ? 0 : null;
  let $totalItemsMax = shelfDepth ? 0 : null;

  if (hasRotation) {
    //Si está rotado calcula de nuevo el width y height y completa valores para imgSize
    const totalBound = _calcRotateTransforms(tmpWidth, tmpHeight, prod.rotation || 0);
    imgSize = {
      ...totalBound,
      w: tmpWidth,
      h: tmpHeight,
    };
    tmpWidth = totalBound.w;
    tmpHeight = totalBound.h;
  }

  const rvItems = [];

  // Calcula subitems
  let _accWidth = 0; /* Va acumulando el width total */
  let _accHeight = 0; /* Va acumulando el height total */
  for (let i = 0; i < facings; i++) {
    const facingBaseX = _round(_accWidth);
    _accHeight = 0; // Por cada columna nueva resetea el total del height
    for (let j = 0; j < stacks; j++) {
      const _id = `${prod.id}${i}${j}`;
      let _img = defaultImage ? defaultImage : null;
      let _w = tmpWidth;
      let _h = tmpHeight;

      let _isLyingDown = stacksLyingDown && j > 0; // Solo están voltados los de abajo
      if (!_stacksLyingDownFirst) _isLyingDown = stacksLyingDown && true; // Todos están voltados los de abajo //DES-4510

      let _forcedImg = false;
      if (_isLyingDown) {
        // Si está tumbado la altura es la profundidad
        _h = prod.itemData.size.d || prod.itemData.size.w;
        if (lyingImage) {
          _img = lyingImage; // Si tiene la imagen la usa
        } else {
          _forcedImg = true; // Marca la imagen como forzada
        }

        // va sumando la cantidad de items que entran en profundidad
        let itemsInDepth = Math.floor(shelfDepth / prod.itemData.size.h) || 1;

        // Si es SRP, multiplicar por depthLoad * frontLoad (exhibitionFormats o unitOfExhibition)
        itemsInDepth *= _getSrpLoadMultiplier(prod);

        if (shelfDepth) $totalItems += itemsInDepth;
      } else if (isRotateY) {
        // Rotado en Y

        _w = tmpDepth;
        _img = rotateYImage;

        // va sumando la cantidad de items que entran en profundidad
        let itemsInDepth = Math.floor(shelfDepth / prod.itemData.size.w) || 1;

        // Si es SRP, multiplicar por depthLoad * frontLoad (exhibitionFormats o unitOfExhibition)
        itemsInDepth *= _getSrpLoadMultiplier(prod);

        if (shelfDepth) $totalItems += itemsInDepth;
      } else {
        // va sumando la cantidad de items que entran en profundidad
        let itemsInDepth = Math.floor(shelfDepth / (prod.itemData.size.d || prod.itemData.size.w)) || 1;

        // Si es SRP, multiplicar por depthLoad * frontLoad (exhibitionFormats o unitOfExhibition)
        itemsInDepth *= _getSrpLoadMultiplier(prod);

        if (shelfDepth) $totalItems += itemsInDepth;
      }

      _w = _round(applyCompressionToFacingWidth(_w, prod));

      const _x = facingBaseX;
      if (j === stacks - 1) _accWidth += _w + sepX; // Acumula el ancho solo con el último producto de la fila

      const _y = _round(_accHeight);
      _accHeight += _h;

      _pushGroupImgItemsForCell({
        rvItems,
        facingIndex: i,
        cellId: _id,
        x: _x,
        y: _y,
        w: _w,
        h: _h,
        imageSrc: _img,
        isLyingDown: _isLyingDown,
        forcedImg: _forcedImg,
        canTileSrp: !hasRotation && !isRotateY && !_isLyingDown,
        srpNoImageTileConfig,
        srpHeightLoad,
      });
    }
  }

  //Limite de carga
  if (prod.limitLoadSize && $totalItems > prod.limitLoadSize) $totalItems = prod.limitLoadSize;

  $totalItemsMax = $totalItems;

  let unMed = _toNumber(prod.itemData.conversion || prod.itemData.unMed || 1);

  //DES-3945 -> Unidad de medida mayor a 1
  const TOTAL_ITEMS_WHITH_CONVERSION_RATIO = true;
  if (TOTAL_ITEMS_WHITH_CONVERSION_RATIO && unMed != null && unMed > 1) {
    $totalItems *= unMed;
    $totalItemsMax *= unMed;
  }

  const totalSize = { w: _round(_accWidth - sepX), h: _round(_accHeight) };

  return { items: rvItems, totalSize, imgSize, $totalItems, $totalItemsMax, __auxCalcGroup: true };
};

export const _auxGetmaxStacks = (prd, parentShelfH, stacksLyingDown = false, rotation = 0) => {
  if (!prd || !prd.itemData) return 1;

  let maxStacksProd = null;

  if (prd.limitStackSize) {
    maxStacksProd = prd.limitStackSize;
  } else {
    maxStacksProd = prd.itemData.maxStacks;

    if (maxStacksProd === 0) maxStacksProd = 1;

    if (maxStacksProd == null) maxStacksProd = 1000; //infinito

    if (!prd.itemData.stackable) maxStacksProd = 1;
  }

  if (false) _log('\n\n[maxStacksProd]', maxStacksProd, prd.itemData.maxStacks);

  if (maxStacksProd == null || maxStacksProd < 2) return 1;

  let _h = prd.itemData.size.h;
  if (rotation % 180) _h = prd.itemData.size.w; /*Si la rotación es horizontal da vuelta w/h*/
  if (rotation % 180 && prd.rotateY) _h = prd.itemData.size.d; /*Si la rotación es horizontal y además hay rotación en Y da vuelta w/h*/ //DES-4510
  if (prd.stacksLyingDown && !_stacksLyingDownFirst) {
    /*Si la rotación es horizontal y además hay rotación en Y da vuelta w/h*/ //DES-4510
    _h = rotation % 180 ? prd.itemData.size.w : prd.itemData.size.d;
  }

  let maxStacksPerShelfH;

  let pallet_height = prd?.isPallet ? DEFAULT_PALLET_HEIGHT : 0;

  /* TODO: id:Vnl7odhSPz  Tener en cuenta productos como "sillas plegables" Porcentaje de apilado / dirección / no stacksLyingDown */
  if (!stacksLyingDown || !_stacksLyingDownFirst) {
    //Apilado normal
    maxStacksPerShelfH = Math.floor((parentShelfH - (DEFAULT_FINGER_SPACE + pallet_height)) / _h);
  } else {
    //Apilado stacksLyingDown pero con el 1er prod de frente
    let _h2 = prd.itemData.size.d || prd.itemData.size.w;
    maxStacksPerShelfH = Math.floor((parentShelfH - (DEFAULT_FINGER_SPACE + pallet_height) - _h) / _h2) + 1;
  }

  return Math.min(maxStacksProd, maxStacksPerShelfH);
};
